/**
 * Module-level cache for markets category data.
 * Persists across component mount/unmount cycles (e.g., navigating to /market/[slug] and back).
 * Uses same pattern as api-cache.ts but specialized for category event data.
 */

export interface CategoryCacheEntry {
  events: any[];
  offset: number;
  hasMore: boolean;
  timestamp: number;
}

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const MAX_ENTRIES = 25;

const categoryCache = new Map<string, CategoryCacheEntry>();
const prefetchInProgress = new Set<string>();

function evictOldEntries(): void {
  if (categoryCache.size <= MAX_ENTRIES) return;
  const entries = Array.from(categoryCache.entries());
  entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
  const toRemove = entries.length - MAX_ENTRIES;
  for (let i = 0; i < toRemove; i++) {
    categoryCache.delete(entries[i][0]);
  }
}

export function getCachedCategory(key: string): CategoryCacheEntry | null {
  return categoryCache.get(key) ?? null;
}

export function isCacheValid(key: string): boolean {
  const cached = categoryCache.get(key);
  if (!cached) return false;
  return Date.now() - cached.timestamp < CACHE_TTL_MS;
}

export function setCategoryCache(key: string, entry: CategoryCacheEntry): void {
  categoryCache.set(key, entry);
  evictOldEntries();
}

export function clearCategoryCache(): void {
  categoryCache.clear();
}

export function isPrefetchInProgress(key: string): boolean {
  return prefetchInProgress.has(key);
}

export function markPrefetchStart(key: string): void {
  prefetchInProgress.add(key);
}

export function markPrefetchEnd(key: string): void {
  prefetchInProgress.delete(key);
}

// ============================================================================
// For You feed cache — separate from category cache because it has different shape
// ============================================================================

export interface ForYouCacheEntry {
  feedItems: any[];
  events: any[];
  frensActivity: any[];
  hasMore: boolean;
  timestamp: number;
}

let forYouCache: ForYouCacheEntry | null = null;

export function getCachedForYou(): ForYouCacheEntry | null {
  if (!forYouCache) return null;
  if (Date.now() - forYouCache.timestamp > CACHE_TTL_MS) return null;
  return forYouCache;
}

export function setCachedForYou(entry: Omit<ForYouCacheEntry, 'timestamp'>): void {
  forYouCache = { ...entry, timestamp: Date.now() };
}
